import { useCallback, useEffect, useRef } from "react";
import {
  advanceLastSyncedSeq,
  loadLastSyncedSeq,
  mergeMessagePatch,
  saveMessage,
  type IdentityRow,
  type MessageRow,
} from "../../../lib/db";
import {
  apiFetchMessageHistory,
  apiFetchSync,
  getStoredJwt,
  type ServerMessageRow,
} from "../../../lib/authApi";
import { newFrameId } from "../../../lib/frame";
import { broadcastTabSync } from "../../../lib/tabSync";
import { dmPeerIdFromConversation, type ConversationRef } from "../../state/chatState";
import type { ConnectionState } from "../../../lib/styxClient";
import type { IdentityKeys } from "../../../lib/crypto";
import type { MutableRefObject } from "react";
import { DM_HISTORY_MERGE_PAGE } from "./dmConstants";

type DecodeFn = (row: ServerMessageRow) => Promise<MessageRow | null>;

export function useDmSync(
  keysRef: MutableRefObject<IdentityKeys | null>,
  selectedConversationRef: MutableRefObject<ConversationRef | null>,
  sendRealtimeCommand: (type: string, payload: Record<string, unknown>, id?: string) => boolean,
  decodeServerMessageRow: DecodeFn,
  identityRow: IdentityRow | null,
  keys: IdentityKeys | null,
  authenticated: boolean,
  conn: ConnectionState,
) {
  const syncInFlightRef = useRef<Promise<void> | null>(null);

  const notifyMessagesChanged = useCallback((peerId: string) => {
    broadcastTabSync({ type: "messages-changed", peerId });
    broadcastTabSync({ type: "idb-changed", scope: "messages", peerId });
  }, []);

  const mergeServerHistory = useCallback(
    async (peerId: string) => {
      const activeKeys = keysRef.current;
      if (!activeKeys) return;
      try {
        let beforeMs: number | undefined;
        let beforeId: string | undefined;
        let changed = false;
        while (true) {
          const { messages } = await apiFetchMessageHistory(peerId, DM_HISTORY_MERGE_PAGE, beforeMs, beforeId);
          if (messages.length === 0) break;
          for (const row of messages) {
            const decoded = await decodeServerMessageRow(row);
            if (!decoded) continue;
            await saveMessage(decoded);
            changed = true;
            if (
              !row.deleted &&
              row.to_id === activeKeys.userId &&
              dmPeerIdFromConversation(selectedConversationRef.current) === peerId
            ) {
              sendRealtimeCommand("msg.delivery_ack", { message_id: row.id }, newFrameId());
              sendRealtimeCommand("msg.read", { message_id: row.id }, newFrameId());
            }
          }
          if (messages.length < DM_HISTORY_MERGE_PAGE) break;
          const oldest = messages[messages.length - 1]!;
          beforeMs = oldest.created_at;
          beforeId = oldest.id;
        }
        if (changed) {
          notifyMessagesChanged(peerId);
        }
      } catch {
        /* offline / history unavailable */
      }
    },
    [decodeServerMessageRow, keysRef, notifyMessagesChanged, selectedConversationRef, sendRealtimeCommand],
  );

  const repairSync = useCallback(async () => {
    while (true) {
      const activeKeys = keysRef.current;
      if (!activeKeys || !getStoredJwt()) return;
      if (syncInFlightRef.current) {
        await syncInFlightRef.current;
        continue;
      }

      const run = (async () => {
        try {
          const after = await loadLastSyncedSeq();
          const { events, messages } = await apiFetchSync(after);
          if (events.length === 0) return;

          let maxSeq = after;
          const changedPeers = new Set<string>();

          for (const event of events) {
            maxSeq = Math.max(maxSeq, event.seq);
            const serverRow = messages[event.message_id];
            if (!serverRow) continue;

            const decoded = await decodeServerMessageRow(serverRow);
            if (decoded) {
              await saveMessage(decoded);
              changedPeers.add(decoded.peerId);
              if (event.event !== "message_created") {
                await mergeMessagePatch(decoded.id, {
                  pending: decoded.from_id === activeKeys.userId ? false : undefined,
                  delivered: serverRow.delivered,
                  read: serverRow.read,
                });
              }
            }
          }

          await advanceLastSyncedSeq(maxSeq);
          for (const peerId of changedPeers) {
            notifyMessagesChanged(peerId);
          }
        } catch {
          /* repair sync is best-effort on reconnect/focus */
        }
      })();

      syncInFlightRef.current = run;
      try {
        await run;
      } finally {
        if (syncInFlightRef.current === run) syncInFlightRef.current = null;
      }
      return;
    }
  }, [decodeServerMessageRow, keysRef, notifyMessagesChanged]);

  useEffect(() => {
    if (!identityRow || !keys || !authenticated) return;
    void repairSync();
  }, [identityRow, keys, repairSync, authenticated, conn]);

  useEffect(() => {
    if (!identityRow || !keys) return;
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void repairSync();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [identityRow, keys, repairSync]);

  return {
    notifyMessagesChanged,
    mergeServerHistory,
    repairSync,
  };
}
